import React, { useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState, Skeletons } from "@/components/common/EmptyState";
import { CreateDrawer } from "@/components/common/CreateDrawer";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, MagnifyingGlass, SquaresFour, Rows, Trash, PencilSimple, Copy, ArrowsClockwise } from "@phosphor-icons/react";

function money(v) {
  if (v == null) return "—";
  if (v >= 100000) return "₹" + (v / 100000).toFixed(1) + "L";
  if (v >= 1000) return "₹" + (v / 1000).toFixed(1) + "K";
  return "₹" + v;
}

function Cell({ col, item }) {
  const v = item[col.key];
  if (col.type === "status") return <StatusBadge status={v} />;
  if (col.type === "priority") return <StatusBadge priority={v} />;
  if (col.type === "money") return <span className="font-mono">{money(v)}</span>;
  if (col.type === "number") return <span className="font-mono">{(v ?? 0).toLocaleString()}</span>;
  if (col.type === "pct") return <span className="font-mono">{v}%</span>;
  if (Array.isArray(v)) return <span className="text-muted-foreground">{v.join(", ")}</span>;
  return <span>{v ?? "—"}</span>;
}

export function ResourceView({ title, subtitle, icon, resource, columns, fields, refreshable }) {
  const { items, isLoading, create, update, remove } = useResource(resource);
  const { isApprover } = useAuth();
  const [view, setView] = useState("cards");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const singular = title.replace(/s$/, "");

  const primary = columns[0];
  const filtered = items.filter((it) => JSON.stringify(it).toLowerCase().includes(q.toLowerCase()));

  const startEdit = (it) => { setEditItem(it); setOpen(true); };
  const startCreate = () => { setEditItem(null); setOpen(true); };
  const duplicate = async (it) => {
    const { id, created_at, _id, ...rest } = it;
    await create.mutateAsync({ ...rest, name: (rest.name || rest.title || rest.code || "Copy") + " (copy)" });
    toast.success("Duplicated");
  };
  const doDelete = async (it) => {
    try { await remove.mutateAsync(it.id); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setConfirm(null);
  };
  const refresh = async (it) => {
    try { await api.post(`/segments/${it.id}/refresh`); toast.success("Audience refreshed"); update.mutate({ id: it.id, body: {} }); }
    catch { toast.error("Refresh failed"); }
  };
  const submit = (body) => (editItem ? update.mutateAsync({ id: editItem.id, body }) : create.mutateAsync(body));

  const Actions = ({ it }) => (
    <div className="flex items-center gap-1">
      {refreshable && <button onClick={(e) => { e.stopPropagation(); refresh(it); }} data-testid="refresh-btn" className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-brand-mint" title="Refresh audience"><ArrowsClockwise size={15} /></button>}
      {fields && <button onClick={(e) => { e.stopPropagation(); startEdit(it); }} data-testid="edit-btn" className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-primary" title="Edit"><PencilSimple size={15} /></button>}
      {fields && <button onClick={(e) => { e.stopPropagation(); duplicate(it); }} data-testid="duplicate-btn" className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-brand-yellow" title="Duplicate"><Copy size={15} /></button>}
      {isApprover && <button onClick={(e) => { e.stopPropagation(); setConfirm(it); }} data-testid="delete-btn" className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-brand-pink" title="Delete"><Trash size={15} /></button>}
    </div>
  );

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} icon={icon}
        actions={fields ? <Button onClick={startCreate} data-testid="add-btn" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><Plus size={16} weight="bold" className="mr-1" /> New</Button> : null} />

      <div className="mb-4 flex items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="resource-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" className="rounded-full pl-9" />
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-full border border-border p-1">
          <button onClick={() => setView("cards")} className={`rounded-full p-1.5 ${view === "cards" ? "bg-secondary text-foreground" : "text-muted-foreground"}`} data-testid="view-cards"><SquaresFour size={18} /></button>
          <button onClick={() => setView("table")} className={`rounded-full p-1.5 ${view === "table" ? "bg-secondary text-foreground" : "text-muted-foreground"}`} data-testid="view-table"><Rows size={18} /></button>
        </div>
      </div>

      {isLoading ? <Skeletons /> : filtered.length === 0 ? (
        <EmptyState title={`No ${title.toLowerCase()} yet`} subtitle="Create your first one to get started." action={fields && <Button onClick={startCreate} className="rounded-full">Create</Button>} />
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((it, i) => (
            <div key={it.id} data-testid={`${resource}-card`} className="group relative rounded-2xl border border-border bg-surface-raised p-5 transition-transform duration-200 hover:-translate-y-1" style={{ animation: `fade-up 0.4s ease-out ${i * 0.03}s both` }}>
              <div className="mb-3 flex items-start justify-between gap-2">
                <p className="font-display text-base font-bold leading-tight">{it[primary.key]}</p>
                {columns.find((c) => c.type === "status") && <StatusBadge status={it[columns.find((c) => c.type === "status").key]} />}
              </div>
              <div className="space-y-1.5 text-sm">
                {columns.slice(1, 5).map((c) => (
                  <div key={c.key} className="flex items-center justify-between gap-2">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</span>
                    <Cell col={c} item={it} />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end border-t border-border/60 pt-2 opacity-0 transition-opacity group-hover:opacity-100"><Actions it={it} /></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-surface-raised">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {columns.map((c) => <th key={c.key} className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{c.label}</th>)}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`${resource}-row`} className="group border-b border-border/60 transition-colors hover:bg-secondary/40">
                  {columns.map((c) => <td key={c.key} className="px-4 py-3"><Cell col={c} item={it} /></td>)}
                  <td className="px-4 py-3"><div className="opacity-0 transition-opacity group-hover:opacity-100"><Actions it={it} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {fields && (
        <CreateDrawer open={open} onOpenChange={setOpen} title={singular} fields={fields} mode={editItem ? "edit" : "create"} initial={editItem}
          submitting={create.isPending || update.isPending} onSubmit={submit} />
      )}
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title={`Delete this ${singular.toLowerCase()}?`}
        description="This action cannot be undone." onConfirm={() => doDelete(confirm)} />
    </div>
  );
}
